import _ from "lodash";
import React, { useEffect, useState } from "react";
import {
  Chip,
  CircularProgress,
  Collapse,
  Grid, IconButton,
  InputAdornment,
  Link,
  MenuItem,
  TextField,
  Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import { Autocomplete } from "@material-ui/lab";
import LinkIcon from "@material-ui/icons/Link";
import { ExpandLess, ExpandMore, LinkOutlined } from "@material-ui/icons";
import { badEmails, fromArrayToOrSentence } from '../../../../helpers/utils';
import useAdminUserSession from "../../../../hooks/useAdminUserSession";
import RiaContactService from "../../services/RiaContactService";
import InvestorsDomainModal from './InvestorsDomainModal';
import CustomSnackbar from "../../../user/shared/CustomSnackbar";
import EmailTextField from "../../shared/Inputs/EmailTextField";
import ContactInfoPhoneTextField from "../../shared/Inputs/ContactInfoPhoneTextField";
import BlackListDomainModal from './BlackListDomainModal';

// Keep in sync with: app/models/ria_contact.rb
const urlRegex = /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,20}([-a-zA-Z0-9@:%_+.~#?&//=!,]*)$/;

const useStyles = makeStyles((theme) => ({
  contentSection: {
    padding: '16px 22px',
    background: '#fff',
    marginBottom: '15px',
    borderRadius: '3px',
  },
  contentSectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    cursor: 'pointer'
  },
  contentSectionHeaderTitle: {
    fontSize: '17px',
    fontWeight: '600',
    color: '#000',
  },
  contentSectionContent: {

  },
  formGroup: {
    padding: theme.spacing(2, 0)
  },
  form: {
    height: '100%',
  },
  field: {
    '& label.Mui-focused': {
      color: '#000',
    },
    '& .MuiInput-underline:after': {
      borderBottomColor: '#000',
    },
    '& .MuiOutlinedInput-root': {
      '&:hover fieldset': {
        borderColor: '#000',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#000',
      },
    },
  },
  tabActive: {
    color: '#1E69E5',
  },
  associationLink: {
    position: 'absolute',
    top: '-10px',
    right: '10px',
    cursor: 'pointer',
    decoration: 'none',
    '&:hover': {
      decoration: 'none',
    }
  },
  inputWithAssociationLink: {
    position: 'relative',
  },
  urlContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
}));

const ContactInfo = (props) => {
  const classes = useStyles();
  const {
    contact,
    handleTabChange,
    handleFieldChange,
    hadleOpen,
    handleOnChangeAutocomplete,
    handleInputChangeAutocomplete,
    openTitleAutocomplete,
    openAutomatedTitleAutocomplete,
    openAchievementsAutocomplete,
    loadingAchievements,
    tab,
    onlyTitle,
    onlyEmail,
    onlyLinkedinUrl,
    potentialLinkedinUrl,
    pastEmails,
    setHasFieldChanges,
    emailValidations,
    onInvestorsForDomainChange,
    onValidationStart,
    onValidationEnd,
    onFieldError
  } = props;

  const { session } = useAdminUserSession();

  const [fileFirstName, setFileFirstName] = useState(contact.file_first_name || '')
  const [fileLastName, setFileLastName] = useState(contact.file_last_name || '')
  const [fileFirstNameOther, setFileFirstNameOther] = useState(contact.file_first_name_other || '')

  const [email, setEmail] = useState(contact.email || '')
  const [emailValidationSuccess, setEmailValidationSuccess] = useState(false);
  const [secondEmail, setSecondEmail] = useState(contact.secondary_email || '')
  const [linkedinUrl, setLinkedinUrl] = useState(contact.linkedinProfileUrl || '')

  const [officePhoneNumber, setOfficePhoneNumber] = useState(contact.office_phone_number || '');
  const [extOfficePhoneNumber, setExtOfficePhoneNumber] = useState(contact.ext_office_phone_number || '');
  const [countryPrefixOfficePhoneNumber, setCountryPrefixOfficePhoneNumber] = useState(contact.country_prefix_office_phone_number || '');

  const [mobilePhoneNumber, setMobilePhoneNumber] = useState(contact.mobile_phone_number || '');
  const [countryPrefixMobilePhoneNumber, setCountryPrefixMobilePhoneNumber] = useState(contact.country_prefix_mobile_phone_number || '');

  const [additionalPhoneNumber, setAdditionalPhoneNumber] = useState(contact.additional_phone_number || '');
  const [extAdditionalPhoneNumber, setExtAdditionalPhoneNumber] = useState(contact.ext_additional_phone_number || '');
  const [countryPrefixAdditionalPhoneNumber, setCountryPrefixAdditionalPhoneNumber] = useState(contact.country_prefix_additional_phone_number || '');

  const defaultPhoneCountry = (contact.main_office_country_iso || 'US').toLowerCase();

  const [personalTeamPage, setPersonalTeamPage] = useState(contact.contact_bio_url || '')
  const [gender, setGender] = useState(contact.gender || '')
  const [warningMessage, setWarningMessage] = useState('');
  const [linkedinErrorMessage, setLinkedinErrorMessage] = useState('')
  const [teamPageErrorMessage, setTeamPageErrorMessage] = useState('')

  const [showInvestorsModal, setShowInvestorsModal] = useState(false);
  const [investorsForDomain, setInvestorsForDomain] = useState([]);
  const [showBlackListModal, setShowBlackListModal] = useState(false);
  const [blackListDomains, setBlackListDomains] = useState([]);

  // Report LinkedIn URL and Team Page URL errors to parent
  useEffect(() => {
    if (onFieldError) onFieldError('linkedinProfileUrl', !!linkedinErrorMessage);
  }, [linkedinErrorMessage]);

  useEffect(() => {
    if (onFieldError) onFieldError('contact_bio_url', !!teamPageErrorMessage);
  }, [teamPageErrorMessage]);

  const validateEmail = (value, otherEmail, setValue) => {
    if (value === '' || value === undefined || value === null) {
      setValue(value);
      setWarningMessage('')
    } else
      if (badEmails.some(x => value.startsWith(x))) {
        setWarningMessage(`Email is not allowed. It cannot starts with ${fromArrayToOrSentence(badEmails)}`)
      }
      else {
        if (value !== otherEmail) {
          setValue(value);
          setWarningMessage('')
        } else {
          setWarningMessage("Email and Secondary Email must differ")
        }
      }
  }

  const validateDomainEmailLogic = async (emailValue) => {
    if (emailValue === '') return;

    try {
      const response = await RiaContactService.validateEmailDomain(
        contact.id,
        session,
        emailValue
      );

      const { status, message, ria_investors, black_list_domains, blocked } = response.data;

      if (status !== 'success') {
        if (blocked) {
                    // Hard block: domain is blacklisted — show blocking modal, clear email
                    setBlackListDomains(black_list_domains || []);
                    setShowBlackListModal(true);
                    clearEmail('email', setEmail);
                } else if (ria_investors && ria_investors.length > 0) {
          setInvestorsForDomain(ria_investors);
          setShowInvestorsModal(true);
        }
      }

      setHasFieldChanges(false);
    } catch (e) {
      setWarningMessage('Validation domain error');
    }
  };

  const validateDomainEmail = async (event, email) => {
    event.persist();
    handleFieldChange(event, email);
  }

  useEffect(() => {
    if (emailValidationSuccess) {
      validateDomainEmailLogic(email)
    }
  }, [emailValidationSuccess]);

  const setValidSecondEmail = (value) => {
    validateEmail(value, email, setSecondEmail);
  }

  const setValidFirstEmail = (value) => {
    const hasChanges = !!value
    setHasFieldChanges(hasChanges);
    validateEmail(value, secondEmail, setEmail);
  }

  const handleChangeLinkedinUrl = (event) => {
    if (event.target.value.length > 250) {
      setLinkedinErrorMessage("Url can't be longer than 250 characters")
    } else {
      setLinkedinUrl(event.target.value)
      setLinkedinErrorMessage('')
    }
  }

  const updateField = (name, value) => {
    handleFieldChange({
      target: { name, value, type: 'text' }
    });
  };

  const clearEmail = (name, setter) => {
    handleFieldChange({
      target: { name, value: '', type: 'text' }
    });
    setter('');

    setHasFieldChanges(false);
  };

  const handleChangeTeamPage = (event) => {
    const value = event.target.value;
    setPersonalTeamPage(value);

    // Maximum length check
    if (value.length > 250) {
      setTeamPageErrorMessage("URL can't be longer than 250 characters");
      return;
    }

    // URL format check
    if (value && !urlRegex.test(value)) {
      setTeamPageErrorMessage("Wrong URL format");
    } else {
      setTeamPageErrorMessage('');
    }
  };

  const handleCloseInvestorsModal = () => {
    setShowInvestorsModal(false);
    clearEmail('email', setEmail)
  };

  const handleConfirmInvestorsModal = () => {
    setShowInvestorsModal(false);
    onInvestorsForDomainChange(investorsForDomain.map((investor) => investor.crd_id));
  };

  const createPhoneChangeHandler = (phoneFieldName, prefixFieldName, setPhone, setPrefix) => ({ phone, prefix }) => {
    if (phone == '') {
      prefix = ''; // Clear prefix if phone is cleared
    }
    setPhone(phone);
    setPrefix(prefix);

    // Actualizar el campo del teléfono
    handleFieldChange({
      target: {
        name: phoneFieldName,
        value: phone,
        type: 'text'
      }
    });

    // Actualizar el campo del prefijo
    handleFieldChange({
      target: {
        name: prefixFieldName,
        value: prefix,
        type: 'text'
      }
    });
  };

  const handleChangeOfficePhoneNumber = createPhoneChangeHandler(
    'office_phone_number',
    'country_prefix_office_phone_number',
    setOfficePhoneNumber,
    setCountryPrefixOfficePhoneNumber
  );

  const handleChangeMobilePhoneNumber = createPhoneChangeHandler(
    'mobile_phone_number',
    'country_prefix_mobile_phone_number',
    setMobilePhoneNumber,
    setCountryPrefixMobilePhoneNumber
  );

  const handleChangeAdditionalPhoneNumber = createPhoneChangeHandler(
    'additional_phone_number',
    'country_prefix_additional_phone_number',
    setAdditionalPhoneNumber,
    setCountryPrefixAdditionalPhoneNumber
  );

  return (
    <div className={classes.contentSection}>
      <div className={classes.contentSectionHeader} onClick={(event) => handleTabChange(event, 0)}>
        <Typography variant='h5' className={`${classes.contentSectionHeaderTitle} ${tab === 0 ? classes.tabActive : ''}`}>Contact Info</Typography>
        {tab === 0 ? <ExpandLess /> : <ExpandMore />}
      </div>
      <Collapse in={tab === 0}>
        <div className={classes.contentSectionContent}>
          <div className={classes.formGroup}>
            <Grid container spacing={3} >
              {
                !onlyTitle ?
                  <React.Fragment>
                    {(session.isSuperAdmin || session.inHouseResearch) &&
                      <>
                        <Grid item md={4} xs={12}>
                          <TextField
                            fullWidth
                            label="FINTRX Updated First Name"
                            name="file_first_name"
                            onBlur={(e) => handleFieldChange(e)}
                            onChange={(e) => setFileFirstName(e.target.value)}
                            value={fileFirstName}
                            variant="outlined"
                            className={classes.field}
                          />
                        </Grid>

                        <Grid item md={4} xs={12}>
                          <TextField
                            fullWidth
                            label="FINTRX Updated Last Name"
                            name="file_last_name"
                            onBlur={(e) => handleFieldChange(e)}
                            onChange={(e) => setFileLastName(e.target.value)}
                            value={fileLastName}
                            variant="outlined"
                            className={classes.field}
                          />
                        </Grid>

                        <Grid item md={4} xs={12}>
                          <TextField
                            fullWidth
                            label="FINTRX Updated First Name Other"
                            name="file_first_name_other"
                            onBlur={(e) => handleFieldChange(e)}
                            onChange={(e) => setFileFirstNameOther(e.target.value)}
                            value={fileFirstNameOther}
                            variant="outlined"
                            className={classes.field}
                          />
                        </Grid>

                      </>
                    }

                    <Grid
                      item
                      md={4}
                      xs={12}
                    >
                      <EmailTextField
                        fullWidth
                        label="Email"
                        name="email"
                        onChange={(e) => {
                          setValidFirstEmail(e.target.value);
                          handleFieldChange(e);
                        }}
                        value={email}
                        variant="outlined"
                        className={classes.field}
                        maxLength={40}
                        clearEmail={() => clearEmail('email', setEmail)}
                        validationData={emailValidations?.email}
                        afterOnBlur={(e) => validateDomainEmail(e, email)}
                        setEmailValidationSuccess={setEmailValidationSuccess}
                        entityData={{ entity_id: contact.id, entity_type: "RiaContact", email_field: "email" }}
                        dataSet={'RIA'}
                        onFieldError={onFieldError}
                      />
                    </Grid>

                    <Grid
                      item
                      md={4}
                      xs={12}
                    >
                      <EmailTextField
                        fullWidth
                        label="Secondary Email"
                        name="secondary_email"
                        onChange={(e) => {
                          setValidSecondEmail(e.target.value);
                          handleFieldChange(e);
                        }}
                        value={secondEmail}
                        variant="outlined"
                        className={classes.field}
                        disabled={onlyEmail || onlyLinkedinUrl}
                        maxLength={40}
                        clearEmail={() => clearEmail('secondary_email', setSecondEmail)}
                        validationData={emailValidations?.secondary_email}
                        afterOnBlur={(e) => handleFieldChange(e, secondEmail)}
                        entityData={{ entity_id: contact.id, entity_type: "RiaContact", email_field: "secondary_email" }}
                        dataSet={'RIA'}
                        onFieldError={onFieldError}
                      />
                    </Grid>
                  </React.Fragment>
                  : null
              }
              <Grid
                item
                md={4}
                xs={12}
                className={classes.inputWithAssociationLink}
              >
                {
                  !onlyTitle ?
                    <React.Fragment>
                      {contact.linkedinProfileUrl != '' ? <Link href={contact.linkedinProfileUrl} target='_blank' rel='noreferrer' className={classes.associationLink}><LinkOutlined /></Link> : ''}
                      <TextField
                        fullWidth
                        label="LinkedIn Url"
                        name="linkedinProfileUrl"
                        onChange={handleChangeLinkedinUrl}
                        onBlur={(e) => handleFieldChange(e, linkedinUrl)}
                        value={linkedinUrl}
                        variant="outlined"
                        className={classes.field}
                        disabled={onlyEmail}
                        inputProps={{
                          maxLength: 255
                        }}
                        helperText="Please use format https://www.linkedin.com/in/username/"
                      />
                      <span style={{ color: 'red', fontSize: '14px', fontWeight: '400' }}>{linkedinErrorMessage}</span>
                    </React.Fragment>
                    :
                    (contact.linkedinProfileUrl != '' ?
                      <div className={classes.urlContainer}>
                        <span>LinkedIn Url</span>
                        <Link href={contact.linkedinProfileUrl} target='_blank' rel='noreferrer'>
                          {contact.linkedinProfileUrl}
                        </Link>
                      </div>
                      : '')
                }
              </Grid>
              {
                !onlyTitle ?
                  <React.Fragment>
                    <Grid
                      item
                      md={4}
                      xs={12}
                    >
                      <ContactInfoPhoneTextField
                        fullWidth
                        label="Office Phone Number"
                        name="office_phone_number"
                        prefix={countryPrefixOfficePhoneNumber}
                        value={officePhoneNumber}
                        onChange={handleChangeOfficePhoneNumber}
                        otherPhones={[
                          { phone: mobilePhoneNumber, prefix: countryPrefixMobilePhoneNumber },
                          { phone: additionalPhoneNumber, prefix: countryPrefixAdditionalPhoneNumber }
                        ]}
                        variant="outlined"
                        onValidationStart={onValidationStart}
                        onValidationEnd={onValidationEnd}
                        onFieldError={onFieldError}
                        className={classes.field}
                        disabled={onlyEmail || onlyLinkedinUrl}
                        defaultCountry={defaultPhoneCountry}
                      />
                    </Grid>
                    <Grid
                      item
                      md={4}
                      xs={12}
                    >
                      <TextField
                        fullWidth
                        label="EXT Office Phone Number"
                        name="ext_office_phone_number"
                        onChange={(e) => setExtOfficePhoneNumber(e.target.value)}
                        onBlur={(e) => handleFieldChange(e, e.target.value)}
                        value={extOfficePhoneNumber}
                        variant="outlined"
                        className={classes.field}
                        disabled={onlyEmail || onlyLinkedinUrl}
                      />
                    </Grid>
                    <Grid
                      item
                      md={4}
                      xs={12}
                    >
                      <ContactInfoPhoneTextField
                        fullWidth
                        label="Mobile Phone Number"
                        name="mobile_phone_number"
                        prefix={countryPrefixMobilePhoneNumber}
                        value={mobilePhoneNumber}
                        onChange={handleChangeMobilePhoneNumber}
                        otherPhones={[
                          { phone: officePhoneNumber, prefix: countryPrefixOfficePhoneNumber },
                          { phone: additionalPhoneNumber, prefix: countryPrefixAdditionalPhoneNumber }
                        ]}
                        variant="outlined"
                        onValidationStart={onValidationStart}
                        onValidationEnd={onValidationEnd}
                        onFieldError={onFieldError}
                        className={classes.field}
                        disabled={onlyEmail || onlyLinkedinUrl}
                        defaultCountry={defaultPhoneCountry}
                      />
                    </Grid>
                    <Grid
                      item
                      md={4}
                      xs={12}
                    >
                      <ContactInfoPhoneTextField
                        fullWidth
                        label="Additional Phone Number"
                        name="additional_phone_number"
                        prefix={countryPrefixAdditionalPhoneNumber}
                        value={additionalPhoneNumber}
                        onChange={handleChangeAdditionalPhoneNumber}
                        otherPhones={[
                          { phone: officePhoneNumber, prefix: countryPrefixOfficePhoneNumber },
                          { phone: mobilePhoneNumber, prefix: countryPrefixMobilePhoneNumber }
                        ]}
                        variant="outlined"
                        onValidationStart={onValidationStart}
                        onValidationEnd={onValidationEnd}
                        onFieldError={onFieldError}
                        className={classes.field}
                        disabled={onlyEmail || onlyLinkedinUrl}
                        defaultCountry={defaultPhoneCountry}
                      />
                    </Grid>
                    <Grid
                      item
                      md={4}
                      xs={12}
                    >
                      <TextField
                        fullWidth
                        label="EXT Additional Phone Number"
                        name="ext_additional_phone_number"
                        onChange={(e) => setExtAdditionalPhoneNumber(e.target.value)}
                        onBlur={(e) => handleFieldChange(e, e.target.value)}
                        value={extAdditionalPhoneNumber}
                        variant="outlined"
                        className={classes.field}
                        disabled={onlyEmail || onlyLinkedinUrl}
                      />
                    </Grid>
                  </React.Fragment>
                  :
                  null
              }
              {
                !onlyEmail && !onlyLinkedinUrl ?
                  <Grid
                    item
                    md={4}
                    xs={12}
                    className={classes.inputWithAssociationLink}
                  >
                    {/* {<Link to='' onClick={handleOpenNewTitleModal} className={classes.associationLink}><Add /></Link>} */}
                    <Autocomplete
                      id="title_id"
                      open={openTitleAutocomplete}
                      onOpen={(event) => hadleOpen(event, 'openTitleAutocomplete', true)}
                      onClose={(event) => hadleOpen(event, 'openTitleAutocomplete', false)}
                      onChange={(event, value) => handleOnChangeAutocomplete(event, 'titleId', value)}
                      // getOptionSelected={(option, value) => option.id === value.id}
                      getOptionSelected={(option, value) => option.id === value.id}
                      getOptionLabel={(option) => option.name || ''}
                      value={contact.titleDefault}
                      options={contact.titleOptions}
                      className={classes.field}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Title"
                          variant="outlined"
                          onChange={(event) => handleInputChangeAutocomplete(event, 'titles')}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <React.Fragment>
                                {/* {loadingAchievements ? <CircularProgress color="inherit" size={20} /> : null} */}
                                {/*change the loading or remove*/}
                                {params.InputProps.endAdornment}
                              </React.Fragment>
                            ),
                          }}
                        />
                      )}
                    />
                  </Grid>
                  : null
              }
              <Grid
                item
                md={4}
                xs={12}
              >
                <TextField
                  fullWidth
                  label="Automated Email"
                  name="automated_email"
                  value={contact.automated_email}
                  variant="outlined"
                  className={classes.field}
                  maxLength={40}
                  dataSet={'RIA'}
                  disabled // Disabled because this is an automated field
                  InputProps={{
                    endAdornment: <>
                      {
                        !_.isEmpty(contact.automated_source) ?
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="go to site"
                              edge="end"
                            >
                              <Link href={contact.automated_source} target={"_blank"}>
                                <LinkIcon />
                              </Link>
                            </IconButton>
                          </InputAdornment> : null
                      }
                    </>
                  }}
                />
              </Grid>
              <Grid
                item
                md={4}
                xs={12}
              >
                <TextField
                  fullWidth
                  label="Automated Phone"
                  name="automated_phone"
                  value={contact.automated_phone}
                  variant="outlined"
                  className={classes.field}
                  disabled // Disabled because this is an automated field
                  InputProps={{
                    endAdornment: <>
                      {
                        !_.isEmpty(contact.automated_source) ?
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="go to site"
                              edge="end"
                            >
                              <Link href={contact.automated_source} target={"_blank"}>
                                <LinkIcon />
                              </Link>
                            </IconButton>
                          </InputAdornment> : null
                      }
                    </>
                  }}
                />
              </Grid>
              <Grid
                item
                md={4}
                xs={12}
              >
                <TextField
                  fullWidth
                  label="Automated Linkedin"
                  name="automated_linkedin"
                  value={contact.automated_linkedin}
                  variant="outlined"
                  className={classes.field}
                  maxLength={400}
                  dataSet={'RIA'}
                  disabled // Disabled because this is an automated field
                  InputProps={{
                    endAdornment: <>
                      {
                        !_.isEmpty(contact.automated_source) ?
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="go to site"
                              edge="end"
                            >
                              <Link href={contact.automated_source} target={"_blank"}>
                                <LinkIcon />
                              </Link>
                            </IconButton>
                          </InputAdornment> : null
                      }
                    </>
                  }}
                />
              </Grid>
              {
                !onlyEmail && !onlyLinkedinUrl ?
                  <Grid
                    item
                    md={4}
                    xs={12}
                    className={classes.inputWithAssociationLink}
                  >
                    <TextField
                      fullWidth
                      label="Automated Title"
                      name="automated_title_name"
                      value={contact.automated_title_name}
                      variant="outlined"
                      className={classes.field}
                      disabled // Disabled because this is an automated field
                      InputProps={{
                        endAdornment: <>
                          {
                            !_.isEmpty(contact.automated_source) ?
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="go to site"
                                  edge="end"
                                >
                                  <Link href={contact.automated_source} target={"_blank"}>
                                    <LinkIcon />
                                  </Link>
                                </IconButton>
                              </InputAdornment> : null
                          }
                        </>
                      }}
                    />
                  </Grid>
                  : null
              }
              {
                !onlyTitle && !onlyEmail && !onlyLinkedinUrl ?
                  <React.Fragment>
                    <Grid
                      item
                      md={4}
                      xs={12}
                    >
                      <TextField
                        select
                        fullWidth
                        value={gender}
                        name='gender'
                        label='Gender'
                        variant='outlined'
                        // onChange={handleFieldChange}
                        onChange={(e) => setGender(e.target.value)}
                        onBlur={(e) => handleFieldChange(e, gender)}
                      >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        <MenuItem value='Male'>Male</MenuItem>
                        <MenuItem value='Female'>Female</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid
                      item
                      md={4}
                      xs={12}
                    >
                      <Autocomplete
                        multiple
                        id="achievements_ids"
                        open={openAchievementsAutocomplete}
                        onOpen={(event) => hadleOpen(event, 'openAchievementsAutocomplete', true)}
                        onClose={(event) => hadleOpen(event, 'openAchievementsAutocomplete', false)}
                        onChange={(event, value) => handleOnChangeAutocomplete(event, 'achievementsIds', value)}
                        getOptionSelected={(option, value) => option.id === value.id}
                        getOptionLabel={(option) => option.name}
                        options={contact.achievementsOptions}
                        value={contact.achievementsDefault}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip classes={{ root: classes.chipRoot, label: classes.chipLabel, deleteIcon: classes.whiteIcon }} label={option.name} {...getTagProps({ index })} />
                          ))
                        }
                        loading={loadingAchievements}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Achievements"
                            variant="outlined"
                            onChange={(event) => handleInputChangeAutocomplete(event, 'achievements')}
                            className={classes.field}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <React.Fragment>
                                  {loadingAchievements ? <CircularProgress color="inherit" size={20} /> : null}
                                  {params.InputProps.endAdornment}
                                </React.Fragment>
                              ),
                            }}
                          />
                        )}
                      />
                    </Grid>
                  </React.Fragment>
                  :
                  null
              }
              <Grid
                item
                md={4}
                xs={12}
                className={classes.inputWithAssociationLink}
              >
                {
                  !onlyTitle ?
                    <React.Fragment>
                      {contact.contact_bio_url != '' ? <Link href={contact.contact_bio_url} target='_blank' rel='noreferrer' className={classes.associationLink}><LinkOutlined /></Link> : ''}
                      <TextField
                        fullWidth
                        label="Personal Team Page"
                        name="contact_bio_url"
                        onChange={handleChangeTeamPage}
                        onBlur={(e) => handleFieldChange(e, contact.contact_bio_url)}
                        value={personalTeamPage}
                        variant="outlined"
                        className={classes.field}
                        disabled={onlyEmail || onlyLinkedinUrl}
                        inputProps={{
                          maxLength: 255
                        }}
                      />
                      <span style={{ color: 'red', fontSize: '14px', fontWeight: '400' }}>{teamPageErrorMessage}</span>
                    </React.Fragment>
                    :
                    (contact.contact_bio_url != '' ?
                      <div className={classes.urlContainer}>
                        <span>Personal Team Page</span>
                        <Link href={contact.contact_bio_url} target='_blank' rel='noreferrer'>
                          {contact.contact_bio_url}
                        </Link>
                      </div>
                      : '')
                }
              </Grid>
            </Grid>
          </div>
          <div style={{ display: 'flex', gap: '20px' }}>
            {
              contact.has_bad_emails ?
                <div>
                  <div className='inaccurate'>Bad Emails</div>
                  <div>
                    {pastEmails?.join(', ')}
                  </div>
                </div>
                : null
            }
            {potentialLinkedinUrl &&
              <div>
                <div className='resolved'>Potential LinkedIn URL</div>
                <div>
                  <Link href={potentialLinkedinUrl} target='_blank' rel='noreferrer'>{potentialLinkedinUrl}</Link>
                </div>
              </div>
            }
          </div>
          {
            warningMessage ? <CustomSnackbar
              open={warningMessage}
              closeHandler={() => {
                setWarningMessage('')
              }}
              message={warningMessage}
              options={{
                severity: 'warning'
              }}
            /> : null
          }
        </div>
      </Collapse>

      {showInvestorsModal && (
        <InvestorsDomainModal
          onConfirm={handleConfirmInvestorsModal}
          onClose={handleCloseInvestorsModal}
        />
      )}

      {showBlackListModal && (
        <BlackListDomainModal
          blackListDomains={blackListDomains}
          onClose={() => setShowBlackListModal(false)}
        />
      )}
    </div>
  )
}

ContactInfo.defaultProps = {
  onlyTitle: false,
  onlyEmail: false,
}

export default ContactInfo;
